// ==================== UPSELL MODAL CONTROLLER ====================

// Cache modal elements to avoid repeated DOM queries
let modalCache = null;
let modalElementsCache = null;

function getModalCache() {
  if (!modalCache) {
    modalCache = document.querySelector("[data-upsell-modal]");
    if (modalCache) {
      modalElementsCache = {
        modal: modalCache,
        title: modalCache.querySelector("[data-upsell-title]"),
        price: modalCache.querySelector("[data-upsell-price]"),
        priceBtn: modalCache.querySelector("[data-upsell-price-btn]"),
        image: modalCache.querySelector("[data-upsell-image]"),
        imageThumbnails: modalCache.querySelector("[data-upsell-image-thumbnails]"),
        atc: modalCache.querySelector("[data-upsell-atc]"),
        rating: modalCache.querySelector("[data-upsell-rating]"),
        ratingPlaceholder: modalCache.querySelector("[data-upsell-rating-placeholder]"),
        styleContainer: modalCache.querySelector("[data-upsell-style]"),
        styleContent: modalCache.querySelector("[data-upsell-style-content]"),
        sizeChart: modalCache.querySelector("[data-upsell-size-chart]"),
        tagline: modalCache.querySelector("[data-upsell-tagline]"),
        description: modalCache.querySelector("[data-upsell-description]"),
        descriptionContent: modalCache.querySelector("[data-upsell-description-content]"),
        variantsContainer: modalCache.querySelector("[data-upsell-variants]"),
        swatches: modalCache.querySelector("[data-upsell-swatches]"),
        priorityBox: modalCache.querySelector("[data-upsell-priority]"),
        priorityCheckbox: modalCache.querySelector("[data-priority-checkbox]"),
        priorityPrice: modalCache.querySelector("[data-priority-price]"),
        priorityImage: modalCache.querySelector("[data-priority-image]")
      };
    }
  }
  return modalElementsCache;
}

// Cache for parsed product data and fetched Judge.me data
const productDataCache = new Map();
const judgeMeCache = new Map();
let upsellOpenRequestId = 0;

function normalizeUpsellImageUrl(url) {
  if (!url) return '';
  return String(url).startsWith('//') ? 'https:' + url : String(url);
}

// Load the hero into the browser cache before revealing the shared modal.
// A short timeout keeps a slow CDN response from trapping the customer.
function preloadUpsellImage(url) {
  const normalizedUrl = normalizeUpsellImageUrl(url);
  if (!normalizedUrl) return Promise.resolve('');

  return new Promise(function (resolve) {
    const image = new Image();
    let settled = false;
    const finish = function () {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      resolve(normalizedUrl);
    };
    const timeout = setTimeout(finish, 3000);

    image.decoding = 'async';
    image.fetchPriority = 'high';
    image.onload = function () {
      if (typeof image.decode === 'function') {
        image.decode().catch(function () {}).finally(finish);
      } else {
        finish();
      }
    };
    image.onerror = finish;
    image.src = normalizedUrl;

    if (image.complete) finish();
  });
}

function setUpsellModalLoading(modal, loading) {
  if (!modal) return;
  modal.classList.toggle('is-loading', loading);
  modal.setAttribute('aria-busy', loading ? 'true' : 'false');
}

function hideUpsellModal(modal) {
  if (!modal) return;
  upsellOpenRequestId += 1;
  modal.classList.add('is-hidden');
  setUpsellModalLoading(modal, false);
  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('upsell-modal-open');
}

// Lazy-fetch upsell payloads from /products/<handle>?view=upsell-data.
// Triggered when a card-product was rendered with `lazy_upsell: true` so
// the data-upsell + data-upsell-family blobs are NOT inlined. Cached as
// in-flight promises so concurrent hover + click on the same handle dedupe.
const upsellFetchCache = new Map();
function fetchUpsellPayload(handle) {
  if (!handle) return Promise.reject(new Error('No product handle'));
  if (upsellFetchCache.has(handle)) return upsellFetchCache.get(handle);

  const promise = fetch('/products/' + encodeURIComponent(handle) + '?view=upsell-data', { credentials: 'same-origin' })
    .then(function (res) {
      if (!res.ok) throw new Error('Upsell fetch failed: ' + res.status);
      return res.text();
    })
    .then(function (htmlText) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = htmlText;
      const script = tmpl.content.querySelector('#upsell-payload');
      if (!script) throw new Error('upsell-payload script not in response');
      const data = JSON.parse(script.textContent);
      if (!data || !data.product) throw new Error('upsell-payload missing product');
      return data;
    })
    .catch(function (err) {
      // Don't poison the cache on failure — let the next click retry.
      upsellFetchCache.delete(handle);
      throw err;
    });

  upsellFetchCache.set(handle, promise);
  return promise;
}

/**
 * Fetch optional CTL family products with bounded concurrency.
 * Skips an already-fetched primary handle, tolerates individual failures,
 * and never rejects the whole picker because one sibling failed.
 */
function fetchFamilyPayloads(handles, options) {
  const opts = options || {};
  const excludeHandle = opts.excludeHandle || null;
  const concurrency = Math.max(1, Number(opts.concurrency) || 4);
  const remaining = [];
  const seen = new Set();
  (handles || []).forEach(function (handle) {
    if (!handle || handle === excludeHandle || seen.has(handle)) return;
    seen.add(handle);
    remaining.push(handle);
  });

  if (!remaining.length) return Promise.resolve([]);

  const results = new Array(remaining.length);
  let nextIndex = 0;

  function worker() {
    if (nextIndex >= remaining.length) return Promise.resolve();
    const index = nextIndex;
    nextIndex += 1;
    const handle = remaining[index];
    return fetchUpsellPayload(handle)
      .then(function (payload) {
        results[index] = payload || null;
      })
      .catch(function (err) {
        console.warn('Upsell modal: optional family product failed', handle, err);
        results[index] = null;
      })
      .then(worker);
  }

  const workers = [];
  for (let i = 0; i < Math.min(concurrency, remaining.length); i += 1) {
    workers.push(worker());
  }
  return Promise.all(workers).then(function () {
    return results.filter(Boolean);
  });
}

// Prefetch on first hover/touch over a lazy-upsell button so the click feels
// instant if the user actually opens the modal. Uses pointer events to fire
// once per button.
document.addEventListener('pointerenter', function (e) {
  const target = e.target;
  if (!target || typeof target.closest !== 'function') return;
  const opener = target.closest('.upsell-open[data-product-handle]');
  if (!opener || opener.dataset.upsellPrefetched === 'true') return;
  opener.dataset.upsellPrefetched = 'true';
  fetchUpsellPayload(opener.dataset.productHandle).catch(function () { /* prefetch errors are fine */ });
}, true);

// Function to populate rating (moved outside to be reusable)
function populateUpsellRating(modal, jdgmRating, jdgmCount, productId, fallbackRating, fallbackRatingMax, fallbackRatingCount) {
  const ratingContainer = modal.querySelector("[data-upsell-rating]");
  const placeholder = modal.querySelector("[data-upsell-rating-placeholder]");
  
  // Hide placeholder when reviews are loaded
  if (placeholder) {
    placeholder.style.display = 'none';
  }
  
  // Check if Judge.me has ratings
  if (jdgmRating > 0 && jdgmCount > 0) {
    // Render Judge.me stars (same structure as jdgm-preview-badge)
    const fullStars = Math.floor(jdgmRating);
    const hasHalfStar = (jdgmRating % 1) >= 0.5;
    
    ratingContainer.innerHTML = `
      <div class="jdgm-widget jdgm-preview-badge jdgm-preview-badge--with-link" data-id="${productId}">
        <div class="jdgm-prev-badge" data-average-rating="${jdgmRating}" data-number-of-reviews="${jdgmCount}">
          <span class="jdgm-prev-badge__stars" data-score="${jdgmRating}" tabindex="0" aria-label="${jdgmRating} stars" role="button">
            ${Array(5).fill(0).map((_, i) => {
              if (i < fullStars) {
                return '<span class="jdgm-star jdgm--on"></span>';
              } else if (i === fullStars && hasHalfStar) {
                return '<span class="jdgm-star jdgm--half"></span>';
              } else {
                return '<span class="jdgm-star"></span>';
              }
            }).join('')}
          </span>
          <span class="jdgm-prev-badge__text">${jdgmCount} reviews</span>
        </div>
      </div>
    `;
    ratingContainer.style.display = 'block';
  } else if (fallbackRating && fallbackRating > 0) {
    // Fall back to standard reviews format
    const rating = parseFloat(fallbackRating) || 0;
    const ratingMax = parseFloat(fallbackRatingMax) || 5;
    const ratingCount = parseInt(fallbackRatingCount) || 0;
    
    // Calculate rating decimal for half stars
    let ratingDecimal = 0;
    const decimal = rating % 1;
    if (decimal >= 0.3 && decimal <= 0.7) {
      ratingDecimal = 0.5;
    } else if (decimal > 0.7) {
      ratingDecimal = 1;
    }
    
    const ratingFloor = Math.floor(rating);
    
    ratingContainer.innerHTML = `
      <div class="rating" role="img" aria-label="${rating} stars, ${ratingCount} reviews">
        <span class="rating-star" style="--rating: ${ratingFloor}; --rating-max: ${ratingMax}; --rating-decimal: ${ratingDecimal};"></span>
      </div>
      <p class="rating-count caption">
        <span aria-hidden="true">(${ratingCount})</span>
      </p>
    `;
    ratingContainer.style.display = 'block';
  } else {
    // No reviews available - hide both placeholder and container
    ratingContainer.style.display = 'none';
    if (placeholder) {
      placeholder.style.display = 'none';
    }
  }
}

// Optimized function to fetch Judge.me data with caching
async function fetchJudgeMeData(productHandle, productId) {
  // Validate inputs
  if (!productHandle || !productId) {
    return { jdgmRating: 0, jdgmCount: 0 };
  }
  
  const cacheKey = `${productHandle}-${productId}`;
  
  // Check cache first
  if (judgeMeCache.has(cacheKey)) {
    return judgeMeCache.get(cacheKey);
  }
  
  try {
    const productUrl = `/products/${productHandle}`;
    const response = await fetch(productUrl, {
      method: 'GET',
      headers: {
        'Accept': 'text/html'
      }
    });
    
    // Check if response is ok
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Try to find Judge.me widget data
    const jdgmWidget = doc.querySelector('.jdgm-prev-badge');
    let jdgmRating = 0;
    let jdgmCount = 0;
    
    if (jdgmWidget) {
      jdgmRating = parseFloat(jdgmWidget.getAttribute('data-average-rating')) || 0;
      jdgmCount = parseInt(jdgmWidget.getAttribute('data-number-of-reviews')) || 0;
    }
    
    const result = { jdgmRating, jdgmCount };
    judgeMeCache.set(cacheKey, result);
    return result;
  } catch (error) {
    // Silently fail - don't log to console in production
    // Only log in development
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('Could not fetch Judge.me data:', error.message);
    }
    // Cache the failure to avoid repeated attempts
    const result = { jdgmRating: 0, jdgmCount: 0 };
    judgeMeCache.set(cacheKey, result);
    return result;
  }
}

// Helper function to render image gallery thumbnails (single horizontal scroll row)
function renderImageGallery(images, container, mainImageElement) {
  if (!container || !images || images.length <= 1) {
    if (container) {
      container.innerHTML = '';
      container.style.display = 'none';
    }
    return;
  }

  container.innerHTML = '';
  container.className = 'upsell-image-thumbnails';
  container.style.display = 'flex';

  images.forEach((imgData, index) => {
    let imgUrl = typeof imgData === 'string' ? imgData : (imgData.src || imgData);
    if (imgUrl.startsWith('//')) {
      imgUrl = 'https:' + imgUrl;
    }

    let thumbnailUrl;
    if (typeof imgData === 'object' && imgData.thumbnail) {
      thumbnailUrl = imgData.thumbnail.startsWith('//') ? 'https:' + imgData.thumbnail : imgData.thumbnail;
    } else {
      thumbnailUrl = imgUrl;
      if (thumbnailUrl.includes('width=')) {
        thumbnailUrl = thumbnailUrl.replace(/width=\d+/, 'width=96');
      } else if (thumbnailUrl.includes('&width=')) {
        thumbnailUrl = thumbnailUrl.replace(/&width=\d+/, '&width=96');
      } else {
        const separator = thumbnailUrl.includes('?') ? '&' : '?';
        thumbnailUrl = thumbnailUrl + separator + 'width=96';
      }
    }

    let fullSizeUrl = imgUrl;
    if (typeof imgData === 'object' && imgData.src) {
      fullSizeUrl = imgData.src.startsWith('//') ? 'https:' + imgData.src : imgData.src;
    }

    const imgAlt = typeof imgData === 'object' && imgData.alt ? imgData.alt : '';

    const thumbnail = document.createElement('button');
    thumbnail.type = 'button';
    thumbnail.className = 'upsell-thumbnail' + (index === 0 ? ' active' : '');
    thumbnail.dataset.imageUrl = fullSizeUrl;
    thumbnail.setAttribute('aria-label', `View image ${index + 1}`);
    thumbnail.innerHTML = `<img src="${thumbnailUrl}" alt="${imgAlt}" loading="lazy" width="48" height="48" decoding="async">`;

    thumbnail.addEventListener('click', () => {
      preloadUpsellImage(fullSizeUrl).then(function (readyImageUrl) {
        mainImageElement.classList.add("fade-out");
        setTimeout(() => {
          mainImageElement.src = readyImageUrl;
          mainImageElement.alt = imgAlt;
          mainImageElement.classList.remove("fade-out");
          mainImageElement.classList.add("fade-in");
          setTimeout(() => mainImageElement.classList.remove("fade-in"), 300);
        }, 150);
      });

      container.querySelectorAll('.upsell-thumbnail').forEach(t => t.classList.remove('active'));
      thumbnail.classList.add('active');

      const thumbnailOffset = thumbnail.offsetLeft;
      const thumbnailWidth = thumbnail.offsetWidth;
      const containerWidth = container.clientWidth;
      const desiredScrollLeft = thumbnailOffset - (containerWidth / 2) + (thumbnailWidth / 2);
      const maxScroll = container.scrollWidth - containerWidth;
      container.scrollTo({
        left: Math.max(0, Math.min(desiredScrollLeft, maxScroll)),
        behavior: 'smooth'
      });
    });

    container.appendChild(thumbnail);
  });
}

// Set ATC button disabled state and text based on variant / product availability
function setUpsellAtcAvailability(atcButton, available) {
  if (!atcButton) return;
  const textEl = atcButton.querySelector(".upsell-atc-text");
  if (available) {
    atcButton.disabled = false;
    atcButton.removeAttribute("aria-disabled");
    if (textEl) textEl.textContent = "ADD TO CART";
  } else {
    atcButton.disabled = true;
    atcButton.setAttribute("aria-disabled", "true");
    if (textEl) textEl.textContent = "Sold out";
  }
}

// Build a family entry from the opened product so its swatch always exists,
// even when category_collection.products is capped at 50 in Liquid.
function familyEntryFromProduct(product) {
  return {
    title: product.title,
    handle: product.handle,
    variant: product.id,
    price: product.price,
    available: product.available !== false,
    variants: product.variants || [],
    option_names: product.option_names || [],
    image: product.image,
    images: product.images || [],
    frame: product.frame || '',
    lens: product.lens || '',
    isLimitedEdition: !!product.isLimitedEdition,
    style: product.style || '',
    product_id: product.product_id,
    jdgm_rating: product.jdgm_rating,
    jdgm_count: product.jdgm_count,
    description: product.description || '',
    short_description: product.short_description || '',
    saleTier: product.saleTier || 0,
    compareAtPrice: product.compareAtPrice || null,
  };
}

function ensureProductInFamily(product, family) {
  const list = Array.isArray(family) ? family.slice() : [];
  const id = String(product.id);
  const handle = String(product.handle || '');
  const exists = list.some(
    (item) =>
      String(item.variant) === id ||
      (handle && String(item.handle || '') === handle)
  );
  if (!exists) list.unshift(familyEntryFromProduct(product));
  return list;
}

function activateUpsellSwatchForProduct(modal, product) {
  if (!modal || !product) return;
  const currentVariantId = String(product.id);
  const currentHandle = String(product.handle || '');
  modal.querySelectorAll('.upsell-swatch, .upsell-swatch-image').forEach((s) => s.classList.remove('active'));

  let toActivate = modal.querySelectorAll(
    `.upsell-swatch[data-variant-id="${currentVariantId}"], .upsell-swatch-image[data-variant-id="${currentVariantId}"]`
  );

  if (!toActivate.length && currentHandle) {
    toActivate = Array.from(modal.querySelectorAll('.upsell-swatch, .upsell-swatch-image')).filter((el) => {
      try {
        const data = JSON.parse(el.dataset.itemData || '{}');
        return String(data.handle || '') === currentHandle || String(data.variant) === currentVariantId;
      } catch (_) {
        return false;
      }
    });
  }

  toActivate.forEach((s) => s.classList.add('active'));
  // Prefer scrolling the desktop image swatch into view when present.
  const focusEl =
    Array.from(toActivate).find((el) => el.classList.contains('upsell-swatch-image')) || toActivate[0];
  if (focusEl && typeof focusEl.scrollIntoView === 'function') {
    requestAnimationFrame(() => {
      focusEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }
}

// Build modal product object from a family item (swatch = other product in category)
function productFromFamilyItem(item) {
  return {
    id: item.variant,
    title: item.title,
    price: item.price,
    image: item.image,
    images: item.images || [],
    variants: item.variants || [],
    option_names: item.option_names || [],
    handle: item.handle,
    description: item.description || '',
    short_description: item.short_description || '',
    product_specs: item.product_specs || '',
    style: item.style || '',
    product_id: item.product_id,
    jdgm_rating: item.jdgm_rating,
    jdgm_count: item.jdgm_count,
    show_priority_upsell: false,
    priority_product: null,
    available: item.available !== false,
    saleTier: item.saleTier || 0,
    compareAtPrice: item.compareAtPrice || null
  };
}

// Open upsell modal handler
document.addEventListener("click", async function (e) {
  const opener = e.target.closest(".upsell-open");
  if (!opener) return;

  e.preventDefault();
  e.stopPropagation();

  const scrollY = window.scrollY || window.pageYOffset;

  const elements = getModalCache();
  if (!elements) return;

  const modal = elements.modal;
  const requestId = ++upsellOpenRequestId;
  // Cart-drawer openers set data-upsell-placement so ATC can tag the line
  // (checkout gift-line-remove looks for properties[_upsell]).
  if (opener.dataset.upsellPlacement) {
    modal.dataset.upsellPlacement = opener.dataset.upsellPlacement;
  } else {
    delete modal.dataset.upsellPlacement;
  }
  const analyticsHost = opener.closest('[data-caffeine-offer]');
  if (analyticsHost) {
    modal.dataset.caffeineOfferType = analyticsHost.dataset.caffeineOfferType || '';
    modal.dataset.caffeineOfferId = analyticsHost.dataset.caffeineOfferId || '';
    modal.dataset.caffeineOfferRevision = analyticsHost.dataset.caffeineOfferRevision || '';
    modal.dataset.caffeineProductId = analyticsHost.dataset.caffeineProductId || '';
    modal.dataset.caffeineSourceProductId = analyticsHost.dataset.caffeineSourceProductId || '';
  }
  [
    'upsellMode',
    'completeLookFormId',
    'completeLookOfferId',
    'completeLookOfferVersion'
  ].forEach(function (key) {
    if (opener.dataset[key]) {
      modal.dataset[key] = opener.dataset[key];
    } else {
      delete modal.dataset[key];
    }
  });
  setUpsellModalLoading(modal, true);
  modal.classList.remove("is-hidden");
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add("upsell-modal-open");

  const inlinePayload = opener.dataset.upsell;
  const lazyHandle = opener.dataset.productHandle;
  const familyHandles = Array.from(
    new Set(
      String(opener.dataset.upsellFamilyHandles || '')
        .split('|')
        .map(function (handle) { return handle.trim(); })
        .filter(Boolean)
    )
  );
  const familyCacheKey = familyHandles.length ? '|family:' + familyHandles.join('|') : '';
  const cacheKey = (inlinePayload || ('handle:' + lazyHandle)) + familyCacheKey;
  // End-of-Season config travels with the fetched payload; inline card payloads
  // predate it, so they fall back to "off" rather than a shared global.
  let product, family, saleConfig = { enabled: false };

  if (productDataCache.has(cacheKey)) {
    const cached = productDataCache.get(cacheKey);
    product = cached.product;
    family = cached.family;
    saleConfig = cached.saleConfig || saleConfig;
  } else if (inlinePayload) {
    try {
      product = JSON.parse(inlinePayload);
      family = JSON.parse(opener.dataset.upsellFamily || '[]');
    } catch (err) {
      console.warn('Upsell modal: Invalid product data (possible unescaped chars in description/specs).', err);
      hideUpsellModal(modal);
      return;
    }
    productDataCache.set(cacheKey, { product, family, saleConfig });
  } else if (lazyHandle) {
    try {
      const data = await fetchUpsellPayload(lazyHandle);
      product = data.product;
      if (data.sale) saleConfig = data.sale;
      if (familyHandles.length) {
        const familyPayloads = await fetchFamilyPayloads(familyHandles, {
          excludeHandle: lazyHandle,
          concurrency: 4,
        });
        family = familyPayloads
          .map(function (payload) { return payload && payload.product; })
          .filter(Boolean)
          .map(familyEntryFromProduct);
      } else {
        family = Array.isArray(data.family) ? data.family : [];
      }
    } catch (err) {
      console.warn('Upsell modal: Failed to fetch lazy upsell data for', lazyHandle, err);
      hideUpsellModal(modal);
      return;
    }
    productDataCache.set(cacheKey, { product, family, saleConfig });
  } else {
    console.warn('Upsell modal: opener has neither data-upsell nor data-product-handle');
    hideUpsellModal(modal);
    return;
  }

  // Ignore a completed request if the customer closed the modal or opened a
  // different product while this product was loading.
  if (requestId !== upsellOpenRequestId || modal.classList.contains('is-hidden')) return;

  const card = opener.closest(".product-card-wrapper");
  const selectedSwatch = card ? card.querySelector(".card__color-swatch.selected") : null;
  const selectedVariantId = selectedSwatch ? selectedSwatch.dataset.variantId : null;
  if (selectedVariantId && String(selectedVariantId) !== String(product.id)) {
    let found = Array.isArray(family)
      ? family.find(function (item) { return String(item.variant) === String(selectedVariantId); })
      : null;

    // Card swatch may be outside the Liquid 50-product family cap — resolve via product URL.
    if (!found && selectedSwatch?.dataset?.url) {
      try {
        const path = new URL(selectedSwatch.dataset.url, window.location.origin).pathname;
        const handleMatch = path.match(/\/products\/([^/?#]+)/);
        if (handleMatch) {
          const data = await fetchUpsellPayload(handleMatch[1]);
          if (data?.product) {
            product = data.product;
            if (data.sale) saleConfig = data.sale;
            if (Array.isArray(data.family) && data.family.length) {
              family = data.family;
            }
            found = true;
          }
        }
      } catch (err) {
        console.warn('Upsell modal: failed to resolve selected card swatch', err);
      }
    } else if (found) {
      product = productFromFamilyItem(found);
    }
  }

  family = ensureProductInFamily(product, family);
  const heroImageReady = preloadUpsellImage(product.image);

  function restoreScroll() {
    window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });
  }
  restoreScroll();
  requestAnimationFrame(restoreScroll);
  setTimeout(restoreScroll, 0);
  setTimeout(restoreScroll, 50);
  setTimeout(restoreScroll, 150);
  setTimeout(restoreScroll, 300);

  // Show placeholder reviews initially
  if (elements.ratingPlaceholder) {
    elements.ratingPlaceholder.style.display = 'block';
  }

  // Fill modal using cached elements
  elements.title.textContent = product.title;
  
  // Fix price display - strip HTML if present
  const priceText = typeof product.price === 'string'
    ? product.price.replace(/<[^>]*>/g, '').trim()
    : String(product.price || '');
  renderUpsellPrice(elements.price, product);
  elements.image.alt = product.title || '';
  elements.atc.dataset.variantId = product.id || product.variant;
  if (product.product_id) modal.dataset.caffeineProductId = product.product_id;
  setUpsellAtcAvailability(elements.atc, product.available !== false);
  if (elements.priceBtn) {
    elements.priceBtn.textContent = priceText;
  }

  // Populate image gallery if product has multiple images
  if (product.images && Array.isArray(product.images) && product.images.length > 1 && elements.imageThumbnails) {
    renderImageGallery(product.images, elements.imageThumbnails, elements.image);
  } else if (elements.imageThumbnails) {
    elements.imageThumbnails.innerHTML = '';
    elements.imageThumbnails.style.display = 'none';
  }

  // Try to get Judge.me data from data attributes first
  let jdgmRating = parseFloat(product.jdgm_rating) || 0;
  let jdgmCount = parseInt(product.jdgm_count) || 0;
  
  // Check if there's already a Judge.me widget on the page for this product
  const existingWidget = document.querySelector(`.jdgm-preview-badge[data-id="${product.product_id || product.id}"] .jdgm-prev-badge`);
  if (existingWidget) {
    jdgmRating = parseFloat(existingWidget.getAttribute('data-average-rating')) || 0;
    jdgmCount = parseInt(existingWidget.getAttribute('data-number-of-reviews')) || 0;
  }
  
  // If still no data, try to fetch from product page (with caching)
  // Only fetch if we have a valid handle
  if ((!jdgmRating || !jdgmCount) && product.handle) {
    fetchJudgeMeData(product.handle, product.product_id || product.id).then(({ jdgmRating: fetchedRating, jdgmCount: fetchedCount }) => {
      populateUpsellRating(modal, fetchedRating, fetchedCount, product.product_id || product.id, product.rating, product.rating_max, product.rating_count);
    }).catch(() => {
      // Silently handle fetch errors - use fallback data if available
      if (product.rating && product.rating > 0) {
        populateUpsellRating(modal, 0, 0, product.product_id || product.id, product.rating, product.rating_max, product.rating_count);
      }
    });
  } else {
    // Use data from attributes or existing widget
    populateUpsellRating(modal, jdgmRating, jdgmCount, product.product_id || product.id, product.rating, product.rating_max, product.rating_count);
  }

  // Populate style
  if (product.style && product.style.trim() !== '') {
    elements.styleContent.textContent = product.style;
    elements.styleContainer.style.display = 'block';
  } else {
    elements.styleContainer.style.display = 'none';
  }

  // Populate size chart
  // Handle product_specs - it might be null, undefined, a string, or an object (rich text metafield)
  let productSpecs = '';
  if (product.product_specs) {
    if (typeof product.product_specs === 'string') {
      productSpecs = product.product_specs.trim();
    } else if (typeof product.product_specs === 'object') {
      // Handle rich text metafield object - try to extract text content
      // Rich text metafields have a 'value' property or might be HTML
      if (product.product_specs.value) {
        // Extract text from HTML if it's HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = product.product_specs.value;
        productSpecs = tempDiv.textContent || tempDiv.innerText || '';
      } else if (product.product_specs.html) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = product.product_specs.html;
        productSpecs = tempDiv.textContent || tempDiv.innerText || '';
      } else {
        // Try to stringify and extract text
        const jsonStr = JSON.stringify(product.product_specs);
        const tempDiv = document.createElement('div');
        tempDiv.textContent = jsonStr;
        productSpecs = tempDiv.textContent || '';
      }
      productSpecs = productSpecs.trim();
    } else {
      // If it's not a string or object, try to convert it
      productSpecs = String(product.product_specs).trim();
    }
  }
  
  // Jewelry uses the jewelry PDP's tagline and bracelet/necklace guide; everything else
  // keeps the eyewear ones. Set both on every open — the modal is shared.
  if (elements.tagline) {
    elements.tagline.textContent = product.is_jewelry
      ? 'HANDMADE JEWELRY  |  1 YEAR WARRANTY'
      : 'HANDMADE ARTISAN EYEWEAR  |  1 YEAR WARRANTY';
  }

  if (product.is_jewelry) {
    elements.sizeChart.innerHTML = product.jewelry_size_chart || '';
    elements.sizeChart.style.display = product.jewelry_size_chart ? 'block' : 'none';
  } else if (product.show_size_chart && product.size_chart_url) {
    // Show size chart only when product_specs metafield is present (same rule as pdp-size-chart snippet)
    renderSizeChart(elements.sizeChart, productSpecs, product.size_chart_url);
    elements.sizeChart.style.display = 'block';
  } else {
    elements.sizeChart.style.display = 'none';
  }

  // Populate description — use product description to match live theme cart upsells.
  const descriptionText = product.description || product.short_description || '';
  if (descriptionText && descriptionText.trim() !== '') {
    elements.descriptionContent.innerHTML = descriptionText;
    elements.description.style.display = 'block';
  } else {
    elements.description.style.display = 'none';
  }

  // Handle priority upsell product
  if (elements.priorityBox && product.show_priority_upsell && product.priority_product) {
    const priorityProduct = product.priority_product;
    if (
      priorityProduct &&
      priorityProduct.variant_id &&
      priorityProduct.price &&
      priorityProduct.image &&
      elements.priorityCheckbox &&
      elements.priorityPrice &&
      elements.priorityImage
    ) {
      elements.priorityCheckbox.dataset.priorityVariantId = priorityProduct.variant_id;
      elements.priorityPrice.textContent = `$${priorityProduct.price}`;
      elements.priorityImage.src = priorityProduct.image;
      elements.priorityImage.alt = priorityProduct.title || 'Skip the Line Priority';
      elements.priorityBox.style.display = 'block';
    } else {
      elements.priorityBox.style.display = 'none';
    }
  } else if (elements.priorityBox) {
    elements.priorityBox.style.display = 'none';
  }

  renderVariantOptions(product, elements.variantsContainer, elements.price, elements.atc);
  elements.swatches.innerHTML = ""; // clear skeletons

  // 1️⃣ Group products — jewelry has no frame family, so skip the lone "Core" swatch
  const groups = groupFamilyProducts(product.is_jewelry ? [] : family);

  // Check if there are any swatches to show
  const hasSwatches = Object.values(groups).some(group => group.length > 0);

  if (hasSwatches) {
    // 2️⃣ Render grouped rows
    renderGroupedSwatches(groups, elements.swatches, modal, saleConfig);
    elements.swatches.style.display = 'flex';
    elements.swatches.style.visibility = 'visible';
    // Set active on the swatch that matches the opened product — never fall back
    // to the first Limited Edition swatch (that made Brown open looking like Sand).
    activateUpsellSwatchForProduct(modal, product);
  } else {
    elements.swatches.style.display = 'none';
  }

  // Ensure form wrapper (swatches + variant options) is always visible so swatches show even when product is sold out
  const formWrapper = modal.querySelector(".upsell_form_wrapper");
  if (formWrapper) {
    formWrapper.style.display = 'block';
    formWrapper.style.visibility = 'visible';
  }

  const heroImageUrl = await heroImageReady;
  if (requestId !== upsellOpenRequestId || modal.classList.contains('is-hidden')) return;
  if (heroImageUrl) {
    elements.image.src = heroImageUrl;
  } else {
    elements.image.removeAttribute('src');
  }

  // Two frames ensure all populated regions have been laid out before the
  // single reveal transition begins.
  requestAnimationFrame(function () {
    requestAnimationFrame(function () {
      if (requestId !== upsellOpenRequestId || modal.classList.contains('is-hidden')) return;
      setUpsellModalLoading(modal, false);
      modal.querySelector('.upsell-modal-box')?.focus({ preventScroll: true });
    });
  });
});

function renderVariantOptions(product, container, priceEl, atcEl) {
  if (!container) return;
  const variants = product.variants;
  if (!variants || variants.length < 2) {
    container.innerHTML = '';
    container.style.display = 'none';
    return;
  }
  const optionNames = product.option_names || [];
  let optionIndex = -1;
  for (let i = 0; i <= 2; i++) {
    const key = 'option' + (i + 1);
    const values = [...new Set(variants.map(v => v[key]).filter(Boolean))];
    if (values.length > 1) {
      optionIndex = i;
      break;
    }
  }
  const label = optionIndex >= 0 && optionNames[optionIndex] ? optionNames[optionIndex] : 'Variant';
  const key = optionIndex >= 0 ? 'option' + (optionIndex + 1) : null;
  container.innerHTML = '';
  const heading = document.createElement('div');
  heading.className = 'upsell-variant-heading';
  heading.textContent = label + ':';
  container.appendChild(heading);
  const row = document.createElement('div');
  row.className = 'upsell-variant-options';
  const currentId = String(product.id || product.variant || variants[0].id);
  variants.forEach((v) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'upsell-variant-option';
    btn.textContent = key ? (v[key] || v.title) : v.title;
    btn.dataset.variantId = v.id;
    btn.dataset.price = typeof v.price === 'string' ? v.price.replace(/<[^>]*>/g, '').trim() : String(v.price || '');
    if (v.available === false) {
      btn.disabled = true;
      btn.classList.add('is-unavailable');
      btn.setAttribute('aria-disabled', 'true');
    }
    if (String(v.id) === currentId) btn.classList.add('active');
    row.appendChild(btn);
  });
  container.appendChild(row);
  container.style.display = 'block';
}

function renderSizeChart(container, productSpecs, sizeChartUrl) {
  if (!sizeChartUrl) return;

  // productSpecs may contain HTML from metafields, insert it as-is
  // The parser will use innerText which strips HTML tags anyway
  const specsContent = productSpecs || '';

  container.innerHTML = `
    <button
      type="button"
      class="product_size_button"
      aria-expanded="false"
      data-sizechart-open
    >
      <svg width="25px" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M0.5 4C0.223858 4 0 4.22386 0 4.5V10.5C0 10.7761 0.223858 11 0.5 11H14.5C14.7761 11 15 10.7761 15 10.5V4.5C15 4.22386 14.7761 4 14.5 4H0.5ZM1 10V5H2.075V7.5C2.075 7.73472 2.26528 7.925 2.5 7.925C2.73472 7.925 2.925 7.73472 2.925 7.5V5H4.075V6.5C4.075 6.73472 4.26528 6.925 4.5 6.925C4.73472 6.925 4.925 6.73472 4.925 6.5V5H6.075V6.5C6.075 6.73472 6.26528 6.925 6.5 6.925C6.73472 6.925 6.925 6.73472 6.925 6.5V5H8.075V7.5C8.075 7.73472 8.26528 7.925 8.5 7.925C8.73472 7.925 8.925 7.73472 8.925 7.5V5H10.075V6.5C10.075 6.73472 10.2653 6.925 10.5 6.925C10.7347 6.925 10.925 6.73472 10.925 6.5V5H12.075V6.5C12.075 6.73472 12.2653 6.925 12.5 6.925C12.7347 6.925 12.925 6.73472 12.925 6.5V5H14V10H1Z"
          fill="#000000"
        />
      </svg>
      Size Chart
    </button>
    
    <!-- Modal -->
    <div class="size-chart-modal" data-sizechart-modal>
      <div class="size-chart-overlay" data-sizechart-overlay></div>
      <div class="size-chart-modal__content">
        <button class="size-chart-modal__close" data-sizechart-close>&times;</button>

        <div class="size_guide_image_container">
          <div class="size-guide-content">
            <!-- Left: Size guide image -->
            <div class="size-guide-wrapper" id="size-guide-wrapper">
              <img
                src="${sizeChartUrl}"
                alt="Size Guide"
                class="size-guide-image"
              />

              <div class="size-label size-a" data-label="A"></div>
              <div class="size-label size-b" data-label="B"></div>
              <div class="size-label size-c" data-label="C"></div>
              <div class="size-label size-d" data-label="D"></div>
            </div>

            <div id="product-specs" class="visible-product-specs">
              <h4>Measurements</h4>
              <p id="ms-a"></p>
              <p id="ms-b"></p>
              <p id="ms-c"></p>
              <p id="ms-d"></p>
            </div>

            <div id="product-specs-full" style="display:none;">
              ${specsContent}
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// Custom function for upsell modal - updates product info when swatch is selected
// These are separate products, not variants, so we update the entire product display
let upsellImageSwapRequestId = 0;

/**
 * Price line for the modal. On sale it becomes struck compare-at + gold price +
 * the tier numeral; otherwise plain text. Built node by node rather than via
 * innerHTML so nothing in the payload can inject markup.
 */
function renderUpsellPrice(el, item) {
  if (!el) return;
  const raw = item ? item.price : '';
  const priceText = typeof raw === 'string' ? raw.replace(/<[^>]*>/g, '').trim() : String(raw || '');
  const compareRaw = item ? item.compareAtPrice : null;
  const compareText = typeof compareRaw === 'string' ? compareRaw.replace(/<[^>]*>/g, '').trim() : '';

  if (!compareText) {
    el.textContent = priceText;
    return;
  }

  el.textContent = '';
  const compare = document.createElement('s');
  compare.className = 'upsell-compare-price';
  compare.textContent = compareText;
  el.appendChild(compare);

  const now = document.createElement('span');
  now.className = 'upsell-price-sale';
  now.textContent = priceText;
  el.appendChild(now);

  const tier = Number(item && item.saleTier) || 0;
  if (tier > 0) {
    el.appendChild(document.createTextNode(' '));
    const chip = document.createElement('span');
    chip.className = 'price-sale-chip';
    chip.textContent = '−' + tier + '%';
    el.appendChild(chip);
  }
}

function updateUpsellProduct(item, modalElement) {
  // Ensure we have the modal - if it's already the modal, use it; otherwise find it
  let modal = modalElement;
  if (!modal || !modal.hasAttribute || !modal.hasAttribute('data-upsell-modal')) {
    modal = document.querySelector("[data-upsell-modal]");
  }
  
  if (!modal) {
    console.error('Modal element not found', modalElement);
    return;
  }
  
  // Always get fresh elements from the modal
  const img = modal.querySelector("[data-upsell-image]");
  const price = modal.querySelector("[data-upsell-price]");
  const btnPrice = modal.querySelector("[data-upsell-price-btn]"); // Optional - may not exist (removed from button)
  const styleContainer = modal.querySelector("[data-upsell-style]");
  const styleContent = modal.querySelector("[data-upsell-style-content]");
  const title = modal.querySelector("[data-upsell-title]");
  const atc = modal.querySelector("[data-upsell-atc]");
  const descriptionContainer = modal.querySelector("[data-upsell-description]");
  const descriptionContent = modal.querySelector("[data-upsell-description-content]");

  // Check for required elements only (btnPrice is intentionally optional and was removed)
  if (!img || !price || !title || !atc) {
    // Silently return if required elements are missing (don't log - btnPrice is optional)
    return;
  }

  // Update title
  if (item.title) {
    title.textContent = item.title;
  }
  
  // Fix price display - strip HTML if present
  if (item.price) {
    const priceText = typeof item.price === 'string' ? item.price.replace(/<[^>]*>/g, '').trim() : item.price;
    renderUpsellPrice(price, item);
    // Only update btnPrice if it exists (it was removed from the button)
    if (btnPrice) {
      btnPrice.textContent = priceText;
    }
  }

  // Update style if available
  if (styleContainer && styleContent) {
    if (item.style && item.style.trim() !== '') {
      styleContent.textContent = item.style;
      styleContainer.style.display = 'block';
    } else {
      styleContainer.style.display = 'none';
    }
  }

  // Smooth fade transition for image change
  if (item.image) {
    // Ensure image URL is complete (add https: if needed)
    let imageUrl = item.image;
    if (imageUrl.startsWith('//')) {
      imageUrl = 'https:' + imageUrl;
    }
    
    const imageSwapRequestId = ++upsellImageSwapRequestId;
    preloadUpsellImage(imageUrl).then(function (readyImageUrl) {
      if (imageSwapRequestId !== upsellImageSwapRequestId || modal.classList.contains('is-hidden')) return;
      img.classList.add("fade-out");

      setTimeout(() => {
        if (imageSwapRequestId !== upsellImageSwapRequestId || modal.classList.contains('is-hidden')) return;
        img.src = readyImageUrl;
        img.alt = item.title || '';
        img.classList.remove("fade-out");
        img.classList.add("fade-in");

        // Remove fade-in class after transition completes
        setTimeout(() => {
          img.classList.remove("fade-in");
        }, 300);
      }, 150); // Half of transition time for fade-out
    });
  }

  // Update image gallery thumbnails if item has images array
  const imageGallery = modal.querySelector("[data-upsell-image-thumbnails]");
  if (item.images && Array.isArray(item.images) && item.images.length > 1 && imageGallery) {
    renderImageGallery(item.images, imageGallery, img);
  } else if (imageGallery) {
    imageGallery.innerHTML = '';
    imageGallery.style.display = 'none';
  }

  // Update variant ID for add to cart (this is the product variant ID)
  if (item.variant) {
    atc.dataset.variantId = item.variant;
  }
  if (item.product_id) modal.dataset.caffeineProductId = item.product_id;
  setUpsellAtcAvailability(atc, item.available !== false);

  const variantsContainer = modal.querySelector("[data-upsell-variants]");
  const priceEl = modal.querySelector("[data-upsell-price]");
  renderVariantOptions(item, variantsContainer, priceEl, atc);

  // Update description — product description first (matches live theme), then short_description.
  // Always show description (no accordion)
  if (descriptionContainer && descriptionContent) {
    // Check both description and short_description properties
    let descriptionText = '';

    if (item.description) {
      descriptionText = typeof item.description === 'string'
        ? item.description.trim()
        : String(item.description).trim();
    }

    // Fallback to short_description if description is empty
    if (!descriptionText && item.short_description) {
      descriptionText = typeof item.short_description === 'string'
        ? item.short_description.trim()
        : String(item.short_description).trim();
    }

    if (descriptionText && descriptionText !== '') {
      descriptionContent.innerHTML = descriptionText;
      descriptionContainer.style.display = 'block';
    } else {
      // If no description for this variant, hide the container
      descriptionContainer.style.display = 'none';
    }
  }
}

// Close modal handler - handles both overlay and close button
document.addEventListener("click", function (e) {
  const closeTrigger = e.target.closest("[data-upsell-close]");
  if (!closeTrigger) return;
  
  // Don't close if clicking inside the modal box content (only close on overlay background or close button)
  const modalBox = e.target.closest(".upsell-modal-box");
  if (modalBox && !e.target.closest(".upsell-close")) {
    return;
  }
  
  const modal = document.querySelector("[data-upsell-modal]");
  if (!modal) return;
  
  hideUpsellModal(modal);
  delete modal.dataset.upsellMode;
  delete modal.dataset.completeLookFormId;
  delete modal.dataset.completeLookOfferId;
  delete modal.dataset.completeLookOfferVersion;
  
  // Reset placeholder when modal closes
  const elements = getModalCache();
  if (elements) {
    if (elements.ratingPlaceholder) {
      elements.ratingPlaceholder.style.display = 'block';
    }
    if (elements.rating) {
      elements.rating.style.display = 'none';
      elements.rating.innerHTML = '';
    }
  }
});

function groupFamilyProducts(products) {
  const groups = {
    "Core": [],
    "Limited Edition": [],
    "Iced": [],
    "XL Sizes": [],
    "Buffalo Horn": [],
    "Gem Cut": [],
    "Round": [],
    "Wood Temple": []
  };

  products.forEach((p) => {
    const title = String(p.title || '').toUpperCase();

    // Exclusive buckets — Signature Gem Cut used to match both "GEM CUT" and
    // "SIGNATURE GEM CUT" → Wood Temple, so the wrong swatch row looked selected.
    if (p.isLimitedEdition) {
      groups['Limited Edition'].push(p);
      return;
    }
    if (title.includes('ICED')) {
      groups['Iced'].push(p);
      return;
    }
    if (title.includes('XL')) {
      groups['XL Sizes'].push(p);
      return;
    }
    if (title.includes('RARE BLANC')) {
      groups['Buffalo Horn'].push(p);
      return;
    }
    if (title.includes('GEM CUT')) {
      groups['Gem Cut'].push(p);
      return;
    }
    if (title.includes('HYDRA OPUS')) {
      groups['Round'].push(p);
      return;
    }
    if (title.includes('WOOD') || title.includes('TEMPLE') || title.includes('HYDRA')) {
      groups['Wood Temple'].push(p);
      return;
    }
    groups['Core'].push(p);
  });

  return groups;
}

// Use event delegation for swatch clicks - single listener on document
let swatchClickHandler = null;

function upsellSwatchItemData(p) {
  return {
    title: p.title,
    handle: p.handle || '',
    variant: p.variant,
    image: p.image,
    images: p.images || [],
    price: p.price,
    style: p.style || '',
    description: p.description || '',
    short_description: p.short_description || '',
    variants: p.variants || [],
    option_names: p.option_names || [],
    available: p.available !== false,
    saleTier: p.saleTier || 0,
    compareAtPrice: p.compareAtPrice || null
  };
}

function upsellSwatchLabel(p, tier) {
  const title = p.title || "This variant";
  const soldOut = p.available === false ? " – Sold out" : "";
  if (tier > 0) return `${title} — on sale, ${tier}% off${soldOut}`;
  return soldOut ? title + soldOut : "";
}

// Mobile colour chip. Identical for grouped and vault rows — the vault carries
// no numerals, only the heading pill.
function buildUpsellColorSwatch(p, tier) {
  const isSoldOut = p.available === false;
  const colorSwatch = document.createElement("button");
  colorSwatch.className = "upsell-swatch" + (isSoldOut ? " upsell-swatch--sold-out" : "");
  colorSwatch.dataset.variantId = p.variant;
  colorSwatch.dataset.itemData = JSON.stringify(upsellSwatchItemData(p));
  const label = upsellSwatchLabel(p, tier);
  if (label) colorSwatch.setAttribute("aria-label", label);

  // Set color styles - use CSS variables from the theme
  if (p.frame && p.lens) {
    colorSwatch.style.setProperty("--left", `var(--${p.frame})`);
    colorSwatch.style.setProperty("--right", `var(--${p.lens})`);
  } else if (p.frame) {
    colorSwatch.style.setProperty("--full", `var(--${p.frame})`);
  } else if (p.lens) {
    colorSwatch.style.setProperty("--full", `var(--${p.lens})`);
  } else {
    colorSwatch.classList.add("upsell-swatch--no-color");
  }

  if (isSoldOut) {
    const soldOutLabel = document.createElement("span");
    soldOutLabel.className = "upsell-swatch-sold-out-label";
    soldOutLabel.textContent = "Sold out";
    colorSwatch.appendChild(soldOutLabel);
  }
  return colorSwatch;
}

function renderGroupedSwatches(groups, wrap, modal, saleConfig) {
  wrap.innerHTML = "";

  const sale = saleConfig || { enabled: false };
  const saleOn = !!sale.enabled;
  // Mobile pulls every sale chip out of its group and into one Archive
  // row at the bottom; desktop keeps them in place with a tier numeral.
  const vaultItems = [];

  Object.entries(groups).forEach(([label, items]) => {
    if (items.length === 0) return;

    const tierOf = (p) => (saleOn ? Number(p.saleTier) || 0 : 0);
    const saleCount = items.filter((p) => tierOf(p) > 0).length;

    // Create heading for this group
    const heading = document.createElement("div");
    heading.className = "upsell-swatch-heading";
    // Whole group moved to the vault: its mobile row would render empty.
    if (saleCount === items.length) heading.classList.add("upsell-swatch-heading--sale-only");
    heading.textContent = label + ":";
    wrap.appendChild(heading);

    // Create containers for mobile (color) and desktop (image) swatches for this group
    const colorSwatchRow = document.createElement("div");
    colorSwatchRow.className = "upsell-swatch-container mobile_show";

    const imageSwatchRow = document.createElement("div");
    imageSwatchRow.className = "upsell-image-swatch-container desktop_show" + (saleCount ? " has-sale" : "");

    items.forEach(p => {
      const tier = tierOf(p);
      const isSoldOut = p.available === false;
      const itemData = upsellSwatchItemData(p);
      const label = upsellSwatchLabel(p, tier);

      // Color swatch for mobile
      if (tier > 0) {
        vaultItems.push(p);
      } else {
        colorSwatchRow.appendChild(buildUpsellColorSwatch(p, tier));
      }

      // Image swatch for desktop
      const imageSwatch = document.createElement("button");
      imageSwatch.className = "upsell-swatch-image" + (isSoldOut ? " upsell-swatch--sold-out" : "");
      imageSwatch.dataset.variantId = p.variant;
      imageSwatch.dataset.itemData = JSON.stringify(itemData);
      if (label) imageSwatch.setAttribute("aria-label", label);

      // Create image element
      const img = document.createElement("img");
      img.src = p.image || '';
      img.loading = "lazy";
      img.className = "swatch-image";
      imageSwatch.appendChild(img);
      if (isSoldOut) {
        const soldOutLabel = document.createElement("span");
        soldOutLabel.className = "upsell-swatch-sold-out-label";
        soldOutLabel.textContent = "Sold out";
        imageSwatch.appendChild(soldOutLabel);
      }

      if (tier > 0) {
        // The chip crops its image with overflow:hidden, so the numeral has to
        // hang off a wrapper rather than off the button itself.
        const saleWrap = document.createElement("span");
        saleWrap.className = "swatch_sale_wrap swatch_sale_wrap--modal";
        saleWrap.appendChild(imageSwatch);
        const pct = document.createElement("span");
        pct.className = "swatch_sale_pct";
        pct.setAttribute("aria-hidden", "true");
        pct.textContent = "−" + tier + "%";
        saleWrap.appendChild(pct);
        imageSwatchRow.appendChild(saleWrap);
      } else {
        imageSwatchRow.appendChild(imageSwatch);
      }
    });

    // A sale-only group has no mobile chips left; an empty row would still add
    // its 12px margin under the (hidden) heading.
    if (colorSwatchRow.children.length) wrap.appendChild(colorSwatchRow);
    wrap.appendChild(imageSwatchRow);
  });

  if (saleOn && vaultItems.length) {
    const maxTier = vaultItems.reduce((max, p) => Math.max(max, Number(p.saleTier) || 0), 0);
    const pillTemplate = vaultItems.length === 1
      ? (sale.pillSingle || '[max]% Off')
      : (sale.pillMulti || 'Up to [max]% Off');

    const vault = document.createElement("div");
    vault.className = "swatch_group_vault mobile_show";

    const vaultHeading = document.createElement("div");
    vaultHeading.className = "upsell-swatch-heading vault_heading";
    vaultHeading.appendChild(document.createTextNode((sale.heading || 'Archive') + ": "));
    const pill = document.createElement("span");
    pill.className = "vault_pill";
    pill.textContent = String(pillTemplate).split('[max]').join(String(maxTier));
    vaultHeading.appendChild(pill);
    vault.appendChild(vaultHeading);

    const vaultRow = document.createElement("div");
    vaultRow.className = "upsell-swatch-container";
    vaultItems.forEach((p) => {
      vaultRow.appendChild(buildUpsellColorSwatch(p, Number(p.saleTier) || 0));
    });
    vault.appendChild(vaultRow);

    wrap.appendChild(vault);
  }

  // Store modal reference on wrap for event delegation
  wrap.dataset.upsellModal = 'true';
}

// Variant option click (same-product variants e.g. lens type)
document.addEventListener("click", function(e) {
  const btn = e.target.closest(".upsell-variant-option");
  if (!btn) return;
  const wrap = btn.closest("[data-upsell-variants]");
  if (!wrap) return;
  e.preventDefault();
  const modal = document.querySelector("[data-upsell-modal]");
  if (!modal) return;
  const atc = modal.querySelector("[data-upsell-atc]");
  const priceEl = modal.querySelector("[data-upsell-price]");
  if (atc) atc.dataset.variantId = btn.dataset.variantId;
  if (priceEl && btn.dataset.price) priceEl.textContent = btn.dataset.price;
  wrap.querySelectorAll(".upsell-variant-option").forEach(b => b.classList.remove("active"));
  btn.classList.add("active");
});

// Single event delegation listener for all swatch clicks (both color and image swatches)
if (!swatchClickHandler) {
  swatchClickHandler = function(e) {
    // Check if click is on swatch or its child elements (like img)
    const swatch = e.target.closest(".upsell-swatch, .upsell-swatch-image");
    if (!swatch) return;
    
    const wrap = swatch.closest("[data-upsell-swatches]");
    if (!wrap) return;
    
    // Find the actual modal container - use document.querySelector to ensure we get it
    const modal = document.querySelector("[data-upsell-modal]");
    if (!modal) {
      console.error('Modal not found');
      return;
    }
    
    // Prevent default only for buttons
    if (swatch.tagName === 'BUTTON') {
      e.preventDefault();
    }
    
    // Remove active from all swatches in the entire modal (both types)
    modal.querySelectorAll(".upsell-swatch, .upsell-swatch-image").forEach(s => s.classList.remove("active"));
    swatch.classList.add("active");
    
    // Parse item data
    const itemDataStr = swatch.dataset.itemData;
    if (!itemDataStr) {
      console.error('Swatch missing itemData', swatch);
      return;
    }
    
    try {
      const itemData = JSON.parse(itemDataStr);
      updateUpsellProduct(itemData, modal);
    } catch (error) {
      console.error('Error parsing swatch data:', error, itemDataStr);
    }
  };
  
  document.addEventListener("click", swatchClickHandler, true); // Use capture phase
}

// Add to cart handler
document.addEventListener("click", async function (e) {
  const btn = e.target.closest("[data-upsell-atc]");
  if (!btn) return;
  if (btn.disabled) return;

  const variantId = btn.dataset.variantId;
  const modal = document.querySelector("[data-upsell-modal]");
  
  // Check if priority checkbox is checked
  const priorityCheckbox = modal?.querySelector("[data-priority-checkbox]");
  const priorityVariantId = priorityCheckbox?.checked ? priorityCheckbox.dataset.priorityVariantId : null;

  // Add loading state
  btn.classList.add("loading");
  btn.setAttribute("aria-disabled", "true");

  try {
    // Build items array - add both products in a single request if priority is checked.
    // Tag cart-drawer cross-sell adds so checkout can show the remove control.
    const placement = modal?.dataset?.upsellPlacement;
    const completeLookMode = modal?.dataset?.upsellMode === "complete_look";
    const analytics = window.CaffeineOfferAnalytics;
    const analyticsInstanceId = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    const offerType = completeLookMode
      ? 'complete_the_look'
      : placement === 'cart_drawer' ? 'cart_upsell' : 'checkout_upsell';
    const offerId = completeLookMode
      ? (modal.dataset.completeLookOfferId || 'complete_the_look')
      : (modal.dataset.caffeineOfferId || offerType);
    const analyticsFields = {
      placement: placement || 'pdp',
      offerType,
      offerId,
      offerRevision: completeLookMode ? modal.dataset.completeLookOfferVersion : modal.dataset.caffeineOfferRevision,
      offerInstanceId: analyticsInstanceId,
      productId: modal.dataset.caffeineProductId,
      sourceProductId: modal.dataset.caffeineSourceProductId,
      variantId,
      productTitle: modal.querySelector('[data-upsell-title]')?.textContent?.trim(),
      role: 'addon'
    };
    analytics?.track('add_attempt', analyticsFields);
    const id = Number(variantId);
    if (!id) {
      throw new Error("Missing variant id");
    }
    let items;
    if (completeLookMode) {
      const productForm = document.getElementById(modal.dataset.completeLookFormId || "");
      if (!productForm) {
        throw new Error("Missing Complete the Look product form");
      }
      const productFormData = new FormData(productForm);
      const productVariantInput = productForm.querySelector('[name="id"]');
      const pendantVariantId = Number(
        productFormData.get("id") || productVariantInput?.value
      );
      if (!pendantVariantId) {
        throw new Error("Missing pendant variant id");
      }

      const quantity = Math.max(1, Number(productFormData.get("quantity")) || 1);
      const instanceId = analyticsInstanceId;
      const completeLookStamp = (role) =>
        JSON.stringify({
          offer: modal.dataset.completeLookOfferId || "",
          instance: instanceId,
          version: modal.dataset.completeLookOfferVersion || "1",
          role
        });
      const pendantProperties = {};
      for (const [key, value] of productFormData.entries()) {
        const propertyMatch = String(key).match(/^properties\[(.+)]$/);
        if (propertyMatch && String(value).length) {
          pendantProperties[propertyMatch[1]] = String(value);
        }
      }

      const pendantItem = {
        id: pendantVariantId,
        quantity,
        properties: {
          ...pendantProperties,
          _complete_look: completeLookStamp("pendant"),
          _caffeine_offer_type: 'complete_the_look',
          _caffeine_offer: offerId,
          _caffeine_offer_revision: modal.dataset.completeLookOfferVersion || '1',
          _caffeine_instance: instanceId,
          _caffeine_role: 'primary',
          _caffeine_placement: 'pdp',
          _caffeine_sid: analytics?.sessionId || ''
        }
      };
      const sellingPlan = Number(productFormData.get("selling_plan"));
      if (sellingPlan) {
        pendantItem.selling_plan = sellingPlan;
      }
      items = [
        pendantItem,
        {
          id,
          quantity,
          properties: {
            _complete_look: completeLookStamp("chain"),
            _caffeine_offer_type: 'complete_the_look',
            _caffeine_offer: offerId,
            _caffeine_offer_revision: modal.dataset.completeLookOfferVersion || '1',
            _caffeine_instance: instanceId,
            _caffeine_role: 'addon',
            _caffeine_placement: 'pdp',
            _caffeine_sid: analytics?.sessionId || ''
          }
        }
      ];
    } else {
      const mainItem = { id, quantity: 1 };
      if (placement) {
        mainItem.properties = {
          _caffeine_offer_type: offerType,
          _caffeine_offer: offerId,
          _caffeine_instance: analyticsInstanceId,
          _caffeine_role: 'recommendation',
          _caffeine_placement: placement,
          _caffeine_sid: analytics?.sessionId || ''
        };
      }
      items = [mainItem];
    }
    if (priorityVariantId) {
      const priorityId = Number(priorityVariantId);
      if (priorityId) items.push({ id: priorityId, quantity: 1 });
    }

    // Add all items to cart in a single request (include sections like other ATC paths)
    const drawerEarly = document.querySelector("cart-drawer");
    const sectionIds =
      drawerEarly && typeof drawerEarly.getSectionsToRender === "function"
        ? drawerEarly.getSectionsToRender().map((s) => s.id)
        : ["cart-drawer", "cart-icon-bubble"];

    const addResponse = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: items,
        sections: sectionIds.join(","),
        sections_url: window.location.pathname
      }),
      credentials: "same-origin"
    });

    const addText = await addResponse.text();
    let addResult;
    try {
      addResult = addText ? JSON.parse(addText) : {};
    } catch (_) {
      addResult = {};
    }

    if (!addResponse.ok) {
      const errMsg = (addResult && addResult.description) || addResult.message || addResponse.statusText || "Unable to add to cart.";
      console.error("Error adding to cart:", addResult, addResponse.status);
      analytics?.track('add_error', { ...analyticsFields, detail: { message: String(errMsg).slice(0, 200) } });
      alert(errMsg);
      return;
    }

    if (addResult.errors && Object.keys(addResult.errors).length) {
      const errMsg = typeof addResult.errors === "string" ? addResult.errors : (addResult.description || "Error adding to cart. Please try again.");
      analytics?.track('add_error', { ...analyticsFields, detail: { message: String(errMsg).slice(0, 200) } });
      alert(errMsg);
      return;
    }

    analytics?.track('conversion', analyticsFields);

    // Prefer sections from the add response; fall back to a root sections fetch.
    // Important: do not redeclare `const modal` inside this try block — that
    // TDZ-shadows the outer `modal` used above for placement/_upsell tagging
    // and throws before the add request runs ("Unable to add to cart").
    const drawer = drawerEarly || document.querySelector("cart-drawer");
    if (!drawer) {
      if (modal) {
        hideUpsellModal(modal);
      }
      return;
    }

    drawer.classList.remove("is-empty");
    let html = addResult.sections;
    if (!html || typeof html !== "object") {
      const res = await fetch(`/?sections=${sectionIds.join(",")}`, { credentials: "same-origin" });
      const resText = await res.text();
      try {
        html = resText ? JSON.parse(resText) : {};
      } catch (_) {
        html = {};
      }
    }

    const payload = {
      id,
      sections: html && typeof html === "object" ? html : {}
    };
    await drawer.renderContents(payload);

    if (modal) {
      hideUpsellModal(modal);
      delete modal.dataset.upsellMode;
      delete modal.dataset.completeLookFormId;
      delete modal.dataset.completeLookOfferId;
      delete modal.dataset.completeLookOfferVersion;
    }
  } catch (error) {
    window.CaffeineOfferAnalytics?.track('add_error', {
      placement: modal?.dataset?.upsellPlacement || 'pdp',
      offerType: modal?.dataset?.upsellMode === 'complete_look' ? 'complete_the_look' : 'cart_upsell',
      offerId: modal?.dataset?.completeLookOfferId || modal?.dataset?.caffeineOfferId,
      variantId,
      detail: { message: String(error?.message || 'add failed').slice(0, 200) }
    });
    console.error("Error adding to cart:", error);
    alert("Unable to add to cart. Please try again.");
  } finally {
    // Remove loading state
    btn.classList.remove("loading");
    btn.removeAttribute("aria-disabled");
  }
});
